/**
 * Pravilo proxyja (T1 §4, T4 §3) kot čista funkcija.
 *
 * To ni optimizacija, ampak pravilnost. `createMediaElementSource()` na
 * cross-origin viru brez `Access-Control-Allow-Origin` **oddaja ničle** —
 * zvok utihne. Ni napake, ni zaznave vnaprej, klica ni mogoče razveljaviti.
 * Zato o poti toka odloči domena in ne adapter: odločitev mora biti
 * testabilna brez brskalnika.
 */

import { effectiveUrl, type Station } from './stations';

export type StreamVia = 'direct' | 'proxy';

export interface StreamRoute {
  /** URL, ki gre v `<audio src>`. */
  url: string;
  via: StreamVia;
  /**
   * Ali vir pošlje CORS glave. Samo pri `true` sme nastati Web Audio graf.
   * Pri direktnem toku tega ne moremo vedeti vnaprej, zato je `false` —
   * domneva v napačno smer bi pomenila tišino brez napake.
   */
  corsSafe: boolean;
}

export interface RouteOptions {
  /**
   * Ali aplikacija dejansko rabi Web Audio graf: vsaj en EQ pas ≠ 0 dB
   * ali vklopljen vizualizator. V fazi 1 vedno `false` — oboje pride v fazi 6.
   */
  needsAudioGraph: boolean;
  /**
   * Ali HLS na tej platformi teče skozi MSE (`AudioCapabilities.hlsMse`).
   *
   * Privzeto `false`: domneva v to smer pomeni graf **manj**, ne graf preveč —
   * in graf preveč je tisti, ki utihne brez napake (R3 §1).
   */
  hlsMse?: boolean;
  /** Osnova za `/api/*`; privzeto `document.baseURI`. Testi jo podajo izrecno. */
  base?: string;
}

function apiBase(base?: string): string {
  if (base !== undefined) return base;
  // Vite gradi z base './' (T1 §10), zato pot ne sme biti absolutna.
  return typeof document === 'undefined' ? 'http://localhost/' : document.baseURI;
}

function apiUrl(path: string, params: Record<string, string>, base?: string): string {
  const url = new URL(path, apiBase(base));
  for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
  return url.toString();
}

export function proxyStreamUrl(target: string, base?: string): string {
  return apiUrl('api/stream', { url: target }, base);
}

export function probeApiUrl(target: string, base?: string): string {
  return apiUrl('api/probe', { url: target }, base);
}

/** Zadnji člen kaskade metapodatkov — ICY iz samega toka (T4 §5). */
export function icyApiUrl(target: string, base?: string): string {
  return apiUrl('api/icy', { url: target }, base);
}

/**
 * Proxy za JSON postaje **brez** CORS (T4 §5).
 *
 * Icecast od 2.4.1 CORS glavo pošilja privzeto, zato gre odjemalec najprej
 * direktno; sem pride šele, ko brskalnik zahtevek zavrne.
 */
export function metaApiUrl(target: string, base?: string): string {
  return apiUrl('api/meta', { url: target }, base);
}

/** Ovitek za normaliziran »izvajalec - naslov« (T4 §6). */
export function artApiUrl(query: string, base?: string): string {
  return apiUrl('api/art', { q: query }, base);
}

/**
 * Katalog postaj prek Workerja (T4 §1, faza 4).
 *
 * Zakaj ne naravnost iz brskalnika, ko pa radio-browser pošilja CORS `*`:
 *
 *   1. Vir prosi za **opisen `User-Agent`** (T3 §1), česar brskalnik ne dovoli
 *      nastaviti — z njegove strani smo neločljivi od kateregakoli skripta
 *   2. Vir ima **en sam strežnik**; predpomnilnik na robu postreže vse
 *      poslušalce z eno poizvedbo navzgor, kar je edina obramba, ki jo imamo
 *   3. **Rezerva** ob izpadu (T3 §1) zahteva drug vir z drugačno obliko
 *      odgovora; normalizacija mora biti na enem mestu, ne v vsakem odjemalcu
 */
export function catalogApiUrl(params: Record<string, string>, base?: string): string {
  return apiUrl('api/catalog', params, base);
}

/**
 * Naslov Icecastovega `status-json.xsl` za dani tok.
 *
 * Pot je vedno v korenu gostitelja, ne poleg toka: tok `…/live/aktual` ima
 * status na `…/status-json.xsl`.
 */
export function icecastStatusUrl(streamUrl: string): string | null {
  try {
    return new URL('/status-json.xsl', streamUrl).toString();
  } catch {
    return null;
  }
}

/**
 * `https://` različica istega URL-ja, ali `null`, če vir ni `http:`.
 *
 * Preizkus mora teči v Workerju (T4 §3.1): brskalnik HTTP zahtevek s HTTPS
 * strani blokira, preden bi ga lahko preizkusil. Preverjanje 2026-07-19 je
 * pokazalo, da radio-browser navaja vse programe RTV Slovenija kot `http://`,
 * HTTPS različice pa delujejo.
 */
export function httpsVariant(url: string): string | null {
  if (!url.startsWith('http://')) return null;
  return `https://${url.slice('http://'.length)}`;
}

/**
 * Tok gre PRIVZETO DIREKTNO. Proxy se vklopi samo, kadar velja vsaj eno:
 *   a) postaja je samo `http:` in nadgradnja sheme ni uspela  (nujno)
 *   b) uporabnik rabi EQ ali vizualizator                     (Web Audio rabi CORS)
 */
export function resolveStreamRoute(station: Station, opts: RouteOptions): StreamRoute {
  const target = effectiveUrl(station);
  const mustProxy = target.startsWith('http:');

  /**
   * ⛔ **HLS ne gre nikoli skozi proxy** (faza 6).
   *
   * `/api/stream` prenese en tok, ne pa drevesa datotek. Skozenj bi šla samo
   * playlista; naslovi segmentov v njej so relativni in bi se razrešili proti
   * `/api/stream`, ne proti postaji — vsak segment bi bil 404. Prepisovanje
   * playlist v Workerju bi bilo drugo, večje delo (in nov razred varnostnih
   * vprašanj: vsak naslov v tuji datoteki bi bil vabilo za naš odhodni promet).
   *
   * Posledica, ki jo je treba poznati: **HLS postaja brez HTTPS ni predvajljiva.**
   * Proxy je bil edina pot okoli mešane vsebine, in te poti tu ni. Obe seed
   * postaji sta zato HTTPS ([T2 §9](../../docs/03-technical/T2-data-model.md)).
   *
   * `corsSafe` je pri HLS odvisen od tega, **kdo hrani medij**: pri `hls.js`
   * element bere `blob:` z našega izvora in graf je varen tudi brez proxyja;
   * pri native HLS bere postajo in graf bi oddal ničle.
   */
  if (station.isHls) {
    return { url: target, via: 'direct', corsSafe: opts.hlsMse === true };
  }

  if (mustProxy || opts.needsAudioGraph) {
    return {
      url: proxyStreamUrl(target, opts.base),
      via: 'proxy',
      corsSafe: true,
    };
  }

  return { url: target, via: 'direct', corsSafe: false };
}
